<script lang="ts">
    import { onDestroy } from 'svelte';
    import { get } from 'svelte/store';
    import { push } from 'svelte-spa-router';
    import { currentUser, authReady } from '$lib/stores/auth';
    import {
        adminStatus,
        adminSyncNow,
        getNCSBESyncRuns,
        humanizeError,
        type AdminStatus,
        type AdminSyncNowResult,
        type NCSBESyncRun,
    } from '$lib/api';
    import AdminNav from '../components/AdminNav.svelte';

    let data: AdminStatus | null = null;
    let error: string | null = null;
    let syncing = false;
    let syncResult: AdminSyncNowResult | null = null;
    let syncError: string | null = null;
    let ncsbeRuns: NCSBESyncRun[] = [];
    let ncsbeError: string | null = null;

    async function load() {
        // Wait for the initial /api/me before judging the session — the store's
        // first emission is null, which would otherwise bounce a logged-in admin.
        if (!get(authReady)) return;
        if (!$currentUser || $currentUser.role !== 'admin') {
            push('/map');
            return;
        }
        try {
            data = await adminStatus();
        } catch (e: any) {
            error = humanizeError(e, 'Failed to load admin status');
        }
        try {
            const resp = await getNCSBESyncRuns();
            ncsbeRuns = resp.runs;
        } catch (e: any) {
            ncsbeError = humanizeError(e, 'Failed to load NCSBE runs');
        }
    }

    async function runSyncNow() {
        if (syncing) return;
        syncing = true;
        syncError = null;
        syncResult = null;
        try {
            syncResult = await adminSyncNow();
            await load();
        } catch (e: any) {
            syncError = e.message || 'Sync failed';
        } finally {
            syncing = false;
        }
    }

    const unsubUser = currentUser.subscribe(() => { load(); });
    const unsubReady = authReady.subscribe(() => { load(); });
    onDestroy(() => { unsubUser(); unsubReady(); });

    function fmtBytes(n: number | null): string {
        if (n == null) return '—';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    }

    function fmtTime(s: string | null): string {
        if (!s) return '—';
        return new Date(s).toLocaleString([], {
            month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    }

    function fmtNum(n: number): string {
        return n.toLocaleString();
    }

    const STATUS_BADGE: Record<string, string> = {
        ok: 'badge-ok',
        error: 'badge-error',
        auth_failed: 'badge-error',
        running: 'badge-warn',
    };
</script>

<div class="admin-page">
    <header class="admin-header">
        <span class="eyebrow">Admin · Status</span>
        <h1>Operations</h1>
        <p class="page-sub">Sync runs, totals, backups, recent activity.</p>
    </header>

    <AdminNav active="status" />

    {#if error}
        <p class="msg msg-error">{error}</p>
    {:else if !data}
        <p class="loading">Loading…</p>
    {:else}
        <!-- Totals: four big stat cards -->
        <section class="block">
            <div class="block-head"><h2>At a glance</h2></div>
            <div class="stats">
                <div class="stat">
                    <span class="stat-num">{fmtNum(data.totals.voters)}</span>
                    <span class="stat-lbl">Voters</span>
                </div>
                <div class="stat">
                    <span class="stat-num">{fmtNum(data.totals.contacts)}</span>
                    <span class="stat-lbl">Contacts logged</span>
                </div>
                <div class="stat">
                    <span class="stat-num">{data.totals.users}</span>
                    <span class="stat-lbl">Active users</span>
                </div>
                <div class="stat stat-accent">
                    <span class="stat-num">{fmtNum(data.geocode_queue)}</span>
                    <span class="stat-lbl">Geocode queue</span>
                </div>
            </div>
        </section>

        <!-- Sync runs -->
        <section class="block">
            <div class="block-head">
                <h2>Sheet sync</h2>
                <button class="btn btn-primary" on:click={runSyncNow} disabled={syncing}>
                    {syncing ? 'Syncing…' : 'Sync now'}
                </button>
            </div>

            {#if syncResult}
                <p class="msg msg-ok">
                    Run complete · status <strong>{syncResult.status}</strong> ·
                    flipped on {syncResult.voters_added} ·
                    updated {syncResult.voters_updated} ·
                    flipped off {syncResult.voters_removed} ·
                    rejected {syncResult.rejected_rows_count}
                </p>
            {/if}
            <p class="muted hint">
                Sheet sync is overlay-only: it flips voters on/off the target list
                and toggles <code>petition_signed</code>. It does not add or remove
                voter records (NCSBE owns the roll).
            </p>
            {#if syncError}
                <p class="msg msg-error">Sync failed: {syncError}</p>
            {/if}

            {#if data.sync_runs.length === 0}
                <p class="muted">No sync runs recorded yet.</p>
            {:else}
                <div class="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Started</th>
                                <th>Status</th>
                                <th class="num" title="Voters newly flipped on to the target list">Flipped on</th>
                                <th class="num" title="Voters flipped off the target list">Flipped off</th>
                                <th class="num">Updated</th>
                                <th class="num">Rejected</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each data.sync_runs as r}
                                <tr>
                                    <td class="mono">{fmtTime(r.started_at)}</td>
                                    <td><span class="badge {STATUS_BADGE[r.status] ?? ''}">{r.status}</span></td>
                                    <td class="num mono">{r.voters_added || ''}</td>
                                    <td class="num mono">{r.voters_removed || ''}</td>
                                    <td class="num mono">{r.voters_updated || ''}</td>
                                    <td class="num mono">{r.rejected_rows_count > 0 ? r.rejected_rows_count : ''}</td>
                                    <td class={r.status === 'ok' ? 'note-cell' : 'error-cell'}>
                                        {r.status === 'ok' ? '' : (r.error_message ?? '')}
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
        </section>

        <!-- NCSBE sync runs -->
        <section class="block">
            <div class="block-head"><h2>NCSBE sync</h2></div>
            <p class="muted hint">
                Weekly Sunday 03:00 ET pull of the statewide voter file.
                NCSBE is the source of truth for who is in HD-51 (adds, updates,
                inactivations, removals).
            </p>
            {#if ncsbeError}
                <p class="msg msg-error">Failed to load NCSBE runs: {ncsbeError}</p>
            {:else if ncsbeRuns.length === 0}
                <p class="muted">No NCSBE syncs recorded yet.</p>
            {:else}
                <div class="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Started</th>
                                <th>Status</th>
                                <th class="num">Added</th>
                                <th class="num">Updated</th>
                                <th class="num">Inactivated</th>
                                <th class="num">Reactivated</th>
                                <th class="num">Removed</th>
                                <th class="num">Rejected</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each ncsbeRuns as r (r.id)}
                                <tr>
                                    <td class="mono">{fmtTime(r.started_at)}</td>
                                    <td><span class="badge {STATUS_BADGE[r.status] ?? ''}">{r.status}</span></td>
                                    <td class="num mono">{r.voters_added || ''}</td>
                                    <td class="num mono">{r.voters_updated || ''}</td>
                                    <td class="num mono">{r.voters_inactivated || ''}</td>
                                    <td class="num mono">{r.voters_reactivated || ''}</td>
                                    <td class="num mono">{r.voters_removed || ''}</td>
                                    <td class="num mono">{r.rejected_rows_count > 0 ? r.rejected_rows_count : ''}</td>
                                    <td class={r.status === 'ok' ? 'note-cell' : 'error-cell'}>
                                        {r.status === 'ok' ? '' : (r.error_message ?? '')}
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
        </section>

        <!-- Last backup -->
        <section class="block">
            <div class="block-head"><h2>Backups</h2></div>
            {#if data.last_backup}
                <dl class="kv">
                    <div><dt>Last completed</dt><dd class="mono">{fmtTime(data.last_backup.finished_at)}</dd></div>
                    <div><dt>Size</dt><dd class="mono">{fmtBytes(data.last_backup.size_bytes)}</dd></div>
                    <div><dt>Destination</dt><dd class="mono dest">{data.last_backup.remote_path ?? '—'}</dd></div>
                </dl>
            {:else}
                <p class="muted">No backup recorded — check that <code>canvass-backup</code> is running.</p>
            {/if}
        </section>

        <!-- Early vote: last ballot file applied -->
        <section class="block">
            <div class="block-head"><h2>Early vote</h2></div>
            {#if data.last_ballot_import}
                <dl class="kv">
                    <div><dt>Last applied</dt><dd class="mono">{fmtTime(data.last_ballot_import.finished_at)}</dd></div>
                    <div><dt>Matched voters</dt><dd class="mono">{fmtNum(data.last_ballot_import.matched_count)}</dd></div>
                    <div><dt>Voted</dt><dd class="mono">{fmtNum(data.last_ballot_import.voted_count)}</dd></div>
                </dl>
            {:else}
                <p class="muted">No ballot file imported yet.</p>
            {/if}
        </section>

        <!-- Recent logins -->
        <section class="block">
            <div class="block-head"><h2>Recent activity</h2></div>
            {#if data.last_logins.length === 0}
                <p class="muted">No recent sessions.</p>
            {:else}
                <div class="table-wrap">
                    <table>
                        <thead>
                            <tr><th>User</th><th>Last active</th><th>From</th></tr>
                        </thead>
                        <tbody>
                            {#each data.last_logins as l}
                                <tr>
                                    <td>{l.username}</td>
                                    <td class="mono">{fmtTime(l.last_used_at)}</td>
                                    <td class="mono ip">{l.ip ?? '—'}</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
        </section>
    {/if}
</div>

<style>
    .admin-page {
        max-width: 1080px;
        margin: 0 auto;
        padding: var(--s-7) var(--s-5) var(--s-8);
        color: var(--ink);
    }
    .admin-header {
        margin-bottom: var(--s-5);
    }
    .admin-header .eyebrow {
        color: var(--leaf-bright);
        display: block;
        margin-bottom: var(--s-2);
    }
    .admin-header h1 {
        font-family: var(--font-display);
        font-size: clamp(2rem, 5vw, 2.8rem);
        margin: 0 0 var(--s-2);
        font-weight: 600;
    }
    .page-sub {
        color: var(--ink-soft);
        font-size: 1rem;
        margin: 0;
    }

    .block {
        margin-top: var(--s-7);
    }
    .block-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--s-3);
        margin-bottom: var(--s-4);
        padding-bottom: var(--s-3);
        border-bottom: 1px solid var(--rule);
    }
    .block-head h2 {
        font-family: var(--font-display);
        font-size: 1.3rem;
        font-weight: 500;
        margin: 0;
    }

    /* Stats */
    .stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
        gap: var(--s-3);
    }
    .stat {
        background: var(--field);
        border: 1px solid var(--rule);
        padding: var(--s-5) var(--s-4);
        position: relative;
        overflow: hidden;
    }
    .stat::before {
        content: '';
        position: absolute;
        left: 0; top: 0; bottom: 0;
        width: 3px;
        background: var(--ink);
    }
    .stat-accent::before { background: var(--sun); }
    .stat-num {
        display: block;
        font-family: var(--font-display);
        font-weight: 500;
        font-size: 2.4rem;
        line-height: 1;
        color: var(--ink);
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.01em;
        margin-bottom: var(--s-2);
    }
    .stat-lbl {
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
    }

    /* Tables */
    .table-wrap {
        background: var(--field);
        border: 1px solid var(--rule);
        overflow-x: auto;
    }
    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
    }
    th, td {
        text-align: left;
        padding: 12px 14px;
        border-bottom: 1px solid var(--rule);
    }
    tbody tr:last-child td {
        border-bottom: none;
    }
    tbody tr:hover {
        background: var(--paper-soft);
    }
    th {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
        background: var(--paper);
        border-bottom: 2px solid var(--rule);
    }
    th.num, td.num { text-align: right; }

    /* Badges */
    .badge {
        display: inline-block;
        padding: 3px 10px;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: lowercase;
        border: 1px solid;
    }
    .badge-ok    { background: var(--leaf-soft);  color: var(--leaf-ink); border-color: var(--leaf-bright); }
    .badge-error { background: var(--alert-soft); color: var(--alert);    border-color: var(--alert); }
    .badge-warn  { background: var(--sun-soft);   color: var(--ink);      border-color: var(--sun); }

    .error-cell { color: var(--alert); max-width: 240px; word-break: break-word; font-size: .78rem; }
    .note-cell  { color: var(--ink-muted); max-width: 240px; word-break: break-word; font-size: .78rem; }

    /* Buttons */
    .btn {
        font-family: var(--font-body);
        font-size: 0.85rem;
        font-weight: 600;
        padding: 8px 16px;
        cursor: pointer;
        border: 1px solid var(--ink);
        background: var(--ink);
        color: var(--paper);
        transition: background 0.15s, color 0.15s;
    }
    .btn:hover:not(:disabled) { background: var(--leaf); border-color: var(--leaf); }
    .btn:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Messages */
    .msg {
        margin: 0 0 var(--s-3);
        padding: 10px 14px;
        font-size: 0.9rem;
        border-left: 3px solid;
    }
    .msg-ok    { background: var(--leaf-soft);  border-color: var(--leaf-bright); color: var(--leaf-ink); }
    .msg-error { background: var(--alert-soft); border-color: var(--alert);       color: var(--alert); }

    .muted   { color: var(--ink-muted); font-style: italic; font-size: 0.9rem; }
    .hint    { margin: 0 0 var(--s-3); }
    .loading { color: var(--ink-soft); font-size: 0.95rem; padding: var(--s-5) 0; }

    /* Key-value list */
    .kv {
        margin: 0;
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 4px var(--s-4);
        font-size: 0.92rem;
    }
    .kv > div { display: contents; }
    .kv dt {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
        align-self: center;
    }
    .kv dd { margin: 0; color: var(--ink); }
    .kv .dest { word-break: break-all; }

    code {
        background: var(--paper);
        padding: 1px 6px;
        border: 1px solid var(--rule);
        font-size: 0.85em;
    }
</style>
